import {Paths} from "@2d-front-monorepo/core";
import {useCallback, useEffect, useRef, useState} from "react";
import {IMvvmContextState, MvvmContextState} from "./MvvmContextState";
import {unwrapMvvmContext} from "./useMvvmTracking";

export interface UseMvvmStringInputResult {
	value: string;
	handleChange: (rawValue: string) => void;
	handleBlur: () => void;
}

export function useMvvmStringInput<TModel extends object, TValue>(
	context: IMvvmContextState<TModel>,
	property: Paths<TModel>,
	valueToString: (value: TValue | undefined) => string,
	valueFromString: (value: string | undefined) => TValue | undefined,
	updateOnChange: boolean,
	onChange?: (value: TValue | undefined) => void,
	onBlur?: () => void,
	transform?: { onChange?: (value: TValue) => TValue; onBlur?: (value: TValue) => TValue }
): UseMvvmStringInputResult {
	// Read the raw context: this hook's re-renders come from the control's own field
	// subscription, and reads through a host facade would subscribe the host instead.
	context = unwrapMvvmContext(context);
	const readValue = context.getValue<TValue>(property);

	const onChangeTransform = transform?.onChange;
	const onBlurTransform = transform?.onBlur;

	// Apply both stages for display purposes (mount + external resync).
	const applyAll = useCallback(
		(v: TValue | undefined): TValue | undefined => {
			if (v == null) return v;
			let next: TValue = v;
			if (onChangeTransform != null) next = onChangeTransform(next);
			if (onBlurTransform != null) next = onBlurTransform(next);
			return next;
		},
		[onChangeTransform, onBlurTransform]
	);

	const [value, setValue] = useState(() => valueToString(applyAll(readValue)));

	// Track the last model value we wrote/synced. This lets us distinguish "external" updates
	// (someone else mutated the bound path — re-apply transforms for display) from "internal"
	// updates we triggered ourselves through handleChange / handleBlur (display is already
	// authoritative — applying transforms again would, for example, trim while the user is
	// still typing).
	const lastSyncedModelRef = useRef<TValue | undefined>(readValue);

	useEffect(() => {
		if (readValue !== lastSyncedModelRef.current) {
			lastSyncedModelRef.current = readValue;
			setValue(valueToString(applyAll(readValue)));
		}
	}, [readValue, valueToString, applyAll]);

	const handleChange = useCallback((rawValue: string): void => {
		const parsed = valueFromString(rawValue);
		const next = parsed != null && onChangeTransform != null ? onChangeTransform(parsed) : parsed;
		// Mirror the transformed form locally only when the transform actually changed the value;
		// otherwise keep the raw keystrokes so we don't disturb in-progress input (e.g. "1.").
		setValue(next !== parsed ? valueToString(next) : rawValue);
		onChange?.(next);
		lastSyncedModelRef.current = next;
		// When not validating per keystroke, notify only — blur enqueues validation.
		context.setValue(property, next, {validate: updateOnChange});
	}, [context, property, valueFromString, valueToString, onChange, updateOnChange, onChangeTransform]);

	const handleBlur = useCallback((): void => {
		if (onBlurTransform != null) {
			const current = context.getValue<TValue>(property);
			if (current != null) {
				const transformed = onBlurTransform(current);
				if (transformed !== current) {
					setValue(valueToString(transformed));
					onChange?.(transformed);
					lastSyncedModelRef.current = transformed;
					context.setValue(property, transformed, {validate: updateOnChange});
				}
			}
		}
		(context as MvvmContextState<TModel>).enqueueValidation(property);
		onBlur?.();
	}, [context, property, valueToString, onChange, onBlur, onBlurTransform, updateOnChange]);

	return {value, handleChange, handleBlur};
}
